import LocalizedStrings from 'localized-strings';

import { KybDocumentType } from '__generated__/GQL';

export const locales = new LocalizedStrings({
  en: {
    activeBadgeLabel: {
      ongoing: 'Ongoing',
      todo: 'To do',
    },
    applicationAccountOpening: {
      body: "We will check your company's documents within 24 business hours.",
      title: 'Account opening',
    },
    applicationFunds: {
      bankingInfo: {
        bankAddress: 'Bank address',
        bankName: 'Bank name',
        bic: 'BIC',
        iban: 'IBAN',
        recipientName: 'Recipient name',
        transferLabel: 'Transfer label (mandatory)',
      },
      body: 'Each shareholder must transfer the amount of their capital contribution as indicated on your draft statutes.',
      copied: 'Copied!',
      copy: 'Copy',
      copyAll: 'Copy everything',
      displayBankingInfo: 'Display banking information',
      modalText: {
        0: 'These bank details are for **the sole use of your Capital Deposit**, and do not correspond to your company’s RIB.',
        1: 'Please enter the name of your transfer as shown below, otherwise **the transfer will be rejected.**',
      },
      modalTitle: 'Banking information',
      title: 'Transfer of your capital contribution',
      you: '(You)',
    },
    applicationRegistration: {
      continue: 'Send documents',
      [KybDocumentType.Kbis]: {
        cta: 'Add my Kbis',
        modalTitle: 'Add your Kbis',
      },
      [KybDocumentType.Statutes]: {
        cta: 'Add my final statutes',
        modalTitle: 'Add your final statutes',
      },
      deleteDocument: 'Delete document',
      file: 'File',
      helpSteps: {
        obtainKbis:
          'Once your company is created, you will receive your **Kbis**.',
        obtainKbisByPartner:
          'Once your company is created, {partner} will send you your **Kbis**.',
        sendToGuichetUnique:
          'Send your file composed of the **Certificate of Deposit** and the **draft statutes** to the',
        sendToGuichetUniqueLink: 'Guichet Unique',
        transferredToPartner:
          'We have sent your **Certificate of Deposit** and **draft statutes** to our partner {partner}.',
        transferToPartner:
          'Send your complete file to the service provider in charge of creating your company.',
        uploadKbis:
          'Add your **Kbis** and your **final statutes** (dated, initialed and signed) to finalize your account opening.',
      },
      modalBody:
        'This document is necessary to finalize the opening of your account',
      modalUploadSubtitle: 'or select a file (max. {sizeInMo}Mo)',
      modalUploadTitleKbis: 'Add your Kbis',
      modalUploadTitleStatutes: 'Add your final statutes',
      seeDocument: 'See document',
      statutesHelpLink: 'Learn more about final statutes',
      title: 'Registration of your company',
      uploadFailureToast: `An error occurred: your documents were not sent. Please try again or contact us if the problem persists.`,
      uploadSuccessToast: 'Your documents have been sent!',
    },
    applicationVerification: {
      body: 'We will check your draft statutes and identity documents within 48 business hours.',
      title: 'Verification of your application',
    },
  },
  fr: {
    activeBadgeLabel: {
      ongoing: 'En cours',
      todo: 'À faire',
    },
    applicationAccountOpening: {
      body: 'Nous vérifions les documents liés à votre entreprise sous 24h ouvrées.',
      title: 'Ouverture du compte',
    },
    applicationFunds: {
      bankingInfo: {
        bankAddress: 'Adresse de la banque',
        bankName: 'Nom de la banque',
        bic: 'BIC',
        iban: 'IBAN',
        recipientName: 'Nom du destinataire',
        transferLabel: 'Intitulé du virement (obligatoire)',
      },
      body: 'Chaque actionnaire doit effectuer le virement correspondant à son apport en capital tel qu’indiqué sur votre projet de statuts.',
      copied: 'Copié !',
      copy: 'Copier',
      copyAll: "Copier l'ensemble",
      displayBankingInfo: 'Afficher les coordonnées bancaires',
      modalText: {
        0: 'Ces coordonnées bancaires sont à **usage unique pour votre Dépôt de Capital**, elles ne correspondent pas au RIB de votre entreprise.',
        1: 'Saisissez l’intitulé de votre virement tel qu’indiqué ci-dessous, **autrement le virement sera rejeté.**',
      },
      modalTitle: 'Coordonnées bancaires',
      title: 'Virement de vos apports',
      you: '(Vous)',
    },
    applicationRegistration: {
      continue: 'Envoyer les documents',
      [KybDocumentType.Kbis]: {
        cta: 'Ajouter mon Kbis',
        modalTitle: 'Ajoutez votre Kbis',
      },
      [KybDocumentType.Statutes]: {
        cta: 'Ajouter mes statuts définitifs',
        modalTitle: 'Ajouter mes statuts définitifs',
      },
      deleteDocument: 'Supprimer le document',
      file: 'Fichier',
      helpSteps: {
        obtainKbis:
          'Une fois votre entreprise créée, vous recevrez votre **Kbis**.',
        obtainKbisByPartner:
          'Une fois votre entreprise créée, {partner} vous transmettra votre **Kbis**',
        sendToGuichetUnique:
          'Envoyez votre dossier composé du **Certificat de dépôt** et du **projet de statuts** au',
        sendToGuichetUniqueLink: 'Guichet Unique',
        transferredToPartner:
          'Nous avons transmis votre **Certificat de dépôt** et **projet de statuts** à notre partenaire {partner}.',
        transferToPartner:
          'Transmettez votre dossier complet au prestataire en charge de la création de votre entreprise.',
        uploadKbis:
          'Ajoutez votre **Kbis** et vos **statuts définitifs (paraphés, datés et signés)** pour finaliser votre ouverture de compte.',
      },
      modalBody:
        'Ce document est nécessaire pour finaliser l’ouverture de votre compte',
      modalUploadSubtitle: 'ou sélectionnez un fichier (max. {sizeInMo}Mo)',
      modalUploadTitleKbis: 'Ajoutez votre Kbis',
      modalUploadTitleStatutes: 'Ajoutez vos statuts définitifs',
      seeDocument: 'Voir le document',
      statutesHelpLink: 'Tout savoir sur les statuts définitifs',
      title: 'Immatriculation de votre entreprise',
      uploadFailureToast: `Une erreur est survenue : vos documents n'ont pas été envoyés. Veuillez réessayer ou nous contacter si le problème persiste.`,
      uploadSuccessToast: 'Vos documents ont bien été envoyés !',
    },
    applicationVerification: {
      body: 'Nous vérifions votre projet de statuts et vos documents d’identité sous 48h ouvrées.',
      title: 'Vérification de votre dossier',
    },
  },
} as const);
